import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent } from './ui/dialog';
import { toast } from 'sonner';
import {
  X, Save, Eye, Globe, History, Undo2, Redo2, 
  Palette, Layout, Sliders, Shield, Smartphone, 
  Monitor, GripVertical, ChevronUp, ChevronDown,
  Upload, Trash2, AlertTriangle, Check
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import FileUpload from './FileUpload';
import axios from 'axios';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

// Theme presets with safe color combinations
const THEME_PRESETS = {
  dark_trap: {
    name: 'Dark Trap',
    primary: '#f97316',
    accent: '#ef4444',
    background: 'solid',
    contrast: 'AA+'
  },
  neon_nights: {
    name: 'Neon Nights',
    primary: '#8b5cf6',
    accent: '#06b6d4',
    background: 'gradient',
    contrast: 'AA'
  },
  gold_hustle: {
    name: 'Gold Hustle', 
    primary: '#fbbf24',
    accent: '#f59e0b',
    background: 'pattern',
    contrast: 'AA+'
  },
  blood_red: {
    name: 'Blood Red',
    primary: '#dc2626',
    accent: '#991b1b', 
    background: 'solid',
    contrast: 'AA'
  },
  forest_shadow: {
    name: 'Forest Shadow',
    primary: '#059669',
    accent: '#047857',
    background: 'gradient',
    contrast: 'AA'
  },
  royal_purple: {
    name: 'Royal Purple',
    primary: '#7c3aed',
    accent: '#5b21b6',
    background: 'pattern',
    contrast: 'AA+'
  },
  ice_blue: {
    name: 'Ice Blue',
    primary: '#0ea5e9',
    accent: '#0284c7',
    background: 'gradient', 
    contrast: 'AA'
  },
  sunset_orange: {
    name: 'Sunset Orange',
    primary: '#ea580c',
    accent: '#c2410c',
    background: 'solid',
    contrast: 'AA+'
  }
};

const FONT_PRESETS = {
  bold_street: { name: 'Bold Street', heading: 'Inter', body: 'Inter', weight: '900' },
  modern_tech: { name: 'Modern Tech', heading: 'Space Grotesk', body: 'Inter', weight: '700' },
  elegant_bold: { name: 'Elegant Bold', heading: 'Playfair Display', body: 'Source Sans Pro', weight: '800' }
};

const DEFAULT_WIDGETS = [
  { id: 'about', type: 'about', enabled: true, order: 0, title: 'About' },
  { id: 'series_shelf', type: 'series_shelf', enabled: true, order: 1, title: 'Featured Series' },
  { id: 'links', type: 'links', enabled: true, order: 2, title: 'Connect' },
  { id: 'fan_wall', type: 'fan_wall', enabled: true, order: 3, title: 'Fan Wall' }
];

const ProfileEditorModal = ({ 
  isOpen, 
  onClose, 
  profileData, 
  profileType, 
  onProfileUpdate 
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('theme');
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop | mobile
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Editor state
  const [liveProfile, setLiveProfile] = useState(null);
  const [draftProfile, setDraftProfile] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // UI state
  const [showHistory, setShowHistory] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [contrastWarning, setContrastWarning] = useState(null);
  
  // Autosave
  const autosaveRef = useRef(null);
  const saveCountRef = useRef(0);

  useEffect(() => {
    if (isOpen && profileData) {
      initializeEditor();
    }
    
    // Cleanup autosave on unmount
    return () => {
      if (autosaveRef.current) {
        clearInterval(autosaveRef.current);
      }
    };
  }, [isOpen, profileData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        handleClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveDraft();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [isOpen]);

  // Autosave setup
  useEffect(() => {
    if (isOpen && isDirty) {
      if (autosaveRef.current) {
        clearInterval(autosaveRef.current);
      }
      
      autosaveRef.current = setTimeout(() => {
        saveDraft(true); // Silent autosave
      }, 10000); // 10 seconds
    }
  }, [isDirty, isOpen]);

  const initializeEditor = () => {
    try {
      // Initialize with current profile data
      const customization = profileData.customization || {};
      const live = customization.live || getDefaultProfile();
      const draft = customization.draft || live;
      const profileHistory = customization.history || [];
      
      setLiveProfile(live);
      setDraftProfile(draft);
      setCurrentProfile({ ...draft });
      setHistory(profileHistory);
      setHistoryIndex(profileHistory.length);
      setIsDirty(false);
      
      // Check for contrast issues
      checkContrast(draft.theme);
      
    } catch (error) {
      console.error('Error initializing editor:', error);
      toast.error('Failed to load profile editor');
    }
  };

  const getDefaultProfile = () => {
    const theme = THEME_PRESETS.dark_trap;
    return {
      version: 1,
      theme: {
        preset: 'dark_trap',
        primary_color: theme.primary,
        accent_color: theme.accent,
        background_style: theme.background,
        font_preset: 'bold_street'
      },
      layout: {
        template: 'single_column',
        widgets: DEFAULT_WIDGETS
      },
      widgets: {
        about: {
          bio: profileData.bio_richtext || '',
          location: '',
          tagline: ''
        },
        series_shelf: {
          featured_ids: profileData.featured_series_ids || [],
          sort_type: 'popular'
        },
        links: profileData.links || {},
        fan_wall: {
          enabled: true,
          auto_approve: true,
          blocked_words: []
        }
      },
      privacy: {
        profile_visibility: 'public',
        widget_visibility: {
          about: 'public',
          series_shelf: 'public', 
          links: 'public',
          fan_wall: 'public'
        }
      },
      updated_at: new Date().toISOString()
    };
  };

  const addToHistory = (profile, note = '') => {
    const newHistoryEntry = {
      version: profile.version || 1,
      snapshot: { ...profile },
      created_at: new Date().toISOString(),
      note: note || `Edit ${new Date().toLocaleTimeString()}`
    };
    
    const newHistory = [...history.slice(0, historyIndex + 1), newHistoryEntry];
    
    // Limit history to 50 entries
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex(historyIndex + 1);
    }
    
    setHistory(newHistory);
  };

  const updateProfile = (updates, addHistory = true) => {
    const newProfile = { 
      ...currentProfile, 
      ...updates,
      version: (currentProfile.version || 1) + 1,
      updated_at: new Date().toISOString()
    };
    
    if (addHistory) {
      addToHistory(currentProfile);
    }
    
    setCurrentProfile(newProfile);
    setIsDirty(true);
    
    // Check contrast when theme changes
    if (updates.theme) {
      checkContrast(newProfile.theme);
    }
  };

  const updateTheme = (themeUpdates) => {
    const newTheme = { ...currentProfile.theme, ...themeUpdates };
    updateProfile({ theme: newTheme });
  };

  const updateLayout = (layoutUpdates) => {
    const newLayout = { ...currentProfile.layout, ...layoutUpdates };
    updateProfile({ layout: newLayout });
  };

  const updateWidget = (widgetId, widgetUpdates) => {
    const newWidgets = { 
      ...currentProfile.widgets, 
      [widgetId]: { ...currentProfile.widgets[widgetId], ...widgetUpdates }
    };
    updateProfile({ widgets: newWidgets });
  };

  const updatePrivacy = (privacyUpdates) => {
    const newPrivacy = { ...currentProfile.privacy, ...privacyUpdates };
    updateProfile({ privacy: newPrivacy });
  };

  const checkContrast = (theme) => {
    // Simple contrast checking - in production, use proper contrast ratio calculation
    const primary = theme.primary_color;
    const accent = theme.accent_color;
    
    // Mock contrast check - replace with real algorithm
    const isDarkPrimary = primary && (primary.includes('1') || primary.includes('2') || primary.includes('3'));
    const isDarkAccent = accent && (accent.includes('1') || accent.includes('2') || accent.includes('3'));
    
    if (isDarkPrimary && isDarkAccent) {
      setContrastWarning('Low contrast detected. Consider using lighter colors for better accessibility.');
    } else {
      setContrastWarning(null);
    }
  };

  const saveDraft = async (silent = false) => {
    if (!currentProfile) return;
    
    setSaving(true);
    try {
      const payload = {
        customization: {
          ...profileData.customization,
          draft: currentProfile,
          draft_updated_at: new Date().toISOString()
        }
      };
      
      await axios.put(`${API}/${profileType}/profile`, payload);
      
      setDraftProfile(currentProfile);
      setIsDirty(false);
      
      if (!silent) {
        toast.success('✅ Draft saved successfully');
      }
      
    } catch (error) {
      console.error('Error saving draft:', error);
      if (!silent) {
        toast.error('Failed to save draft');
      }
    } finally {
      setSaving(false);
    }
  };

  const publishProfile = async () => {
    setPublishing(true);
    try {
      // Add current live to history
      if (liveProfile) {
        addToHistory(liveProfile, 'Published version');
      }
      
      const payload = {
        customization: {
          live: currentProfile,
          draft: null, // Clear draft after publishing
          history: history,
          published_at: new Date().toISOString()
        }
      };
      
      await axios.put(`${API}/${profileType}/profile`, payload);
      
      setLiveProfile(currentProfile);
      setDraftProfile(null);
      setIsDirty(false);
      
      toast.success('🎉 Profile published successfully!');
      
      // Update parent component
      if (onProfileUpdate) {
        onProfileUpdate(currentProfile);
      }
      
    } catch (error) {
      console.error('Error publishing profile:', error);
      toast.error('Failed to publish profile');
    } finally {
      setPublishing(false);
    }
  };

  const discardChanges = () => {
    if (!isDirty) {
      handleClose();
      return;
    }
    setShowDiscardConfirm(true);
  };

  const confirmDiscard = () => {
    setCurrentProfile({ ...liveProfile });
    setIsDirty(false);
    setShowDiscardConfirm(false);
    toast.success('Changes discarded');
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousProfile = history[newIndex].snapshot;
      setCurrentProfile({ ...previousProfile });
      setHistoryIndex(newIndex);
      setIsDirty(true);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextProfile = history[newIndex].snapshot;
      setCurrentProfile({ ...nextProfile });
      setHistoryIndex(newIndex);
      setIsDirty(true);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleWidgetReorder = (result) => {
    if (!result.destination) return;

    const widgets = Array.from(currentProfile.layout.widgets);
    const [reorderedWidget] = widgets.splice(result.source.index, 1);
    widgets.splice(result.destination.index, 0, reorderedWidget);

    // Update order values
    const updatedWidgets = widgets.map((widget, index) => ({
      ...widget,
      order: index
    }));

    updateLayout({ widgets: updatedWidgets });
  };

  const toggleWidget = (widgetId) => {
    const widgets = currentProfile.layout.widgets.map(widget =>
      widget.id === widgetId 
        ? { ...widget, enabled: !widget.enabled }
        : widget
    );
    updateLayout({ widgets });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Top Bar */}
      <div className="h-14 bg-gray-900 border-b border-gray-700 flex items-center justify-between px-6">
        {/* Left: Title and Status */}
        <div className="flex items-center space-x-4">
          <h1 className="text-lg font-bold text-white">Profile Editor</h1>
          <div className="flex items-center space-x-2">
            {isDirty && (
              <Badge className="bg-yellow-600 text-white text-xs">
                Unsaved changes
              </Badge>
            )}
            {saving && (
              <Badge className="bg-blue-600 text-white text-xs">
                Saving...
              </Badge>
            )}
          </div>
        </div>

        {/* Center: Actions */}
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={undo}
            disabled={historyIndex <= 0}
            className="text-gray-400 hover:text-white"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            className="text-gray-400 hover:text-white"
          >
            <Redo2 className="w-4 h-4" />
          </Button>

          <div className="h-6 w-px bg-gray-600 mx-2" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory(true)}
            className="text-gray-400 hover:text-white"
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
        </div>

        {/* Right: Main Actions */}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={saveDraft}
            disabled={!isDirty || saving}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`/${profileType}/${profileData.handle}?preview=draft`, '_blank')}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>

          <Button
            onClick={publishProfile}
            disabled={!currentProfile || publishing}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
          >
            {publishing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Publishing...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4 mr-2" />
                Publish
              </>
            )}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={discardChanges}
            className="text-gray-400 hover:text-white"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Discard
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex h-[calc(100vh-56px)]">
        {/* Left Panel - Editor Controls */}
        <div className="w-96 bg-gray-900 border-r border-gray-700 flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="w-full bg-gray-800 border-b border-gray-700 rounded-none h-12">
              <TabsTrigger value="theme" className="flex-1">
                <Palette className="w-4 h-4 mr-2" />
                Theme
              </TabsTrigger>
              <TabsTrigger value="layout" className="flex-1">
                <Layout className="w-4 h-4 mr-2" />
                Layout
              </TabsTrigger>
              <TabsTrigger value="widgets" className="flex-1">
                <Sliders className="w-4 h-4 mr-2" />
                Widgets
              </TabsTrigger>
              <TabsTrigger value="privacy" className="flex-1">
                <Shield className="w-4 h-4 mr-2" />
                Privacy
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto p-4">
              <TabsContent value="theme" className="mt-0">
                <ThemeEditor 
                  theme={currentProfile?.theme || {}}
                  onThemeUpdate={updateTheme}
                  contrastWarning={contrastWarning}
                />
              </TabsContent>

              <TabsContent value="layout" className="mt-0">
                <LayoutEditor
                  layout={currentProfile?.layout || {}}
                  onLayoutUpdate={updateLayout}
                  onWidgetReorder={handleWidgetReorder}
                  onWidgetToggle={toggleWidget}
                />
              </TabsContent>

              <TabsContent value="widgets" className="mt-0">
                <WidgetsEditor
                  widgets={currentProfile?.widgets || {}}
                  layout={currentProfile?.layout || {}}
                  onWidgetUpdate={updateWidget}
                  profileType={profileType}
                />
              </TabsContent>

              <TabsContent value="privacy" className="mt-0">
                <PrivacyEditor
                  privacy={currentProfile?.privacy || {}}
                  onPrivacyUpdate={updatePrivacy}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Right Panel - Live Preview */}
        <div className="flex-1 bg-gray-950 flex flex-col">
          {/* Preview Controls */}
          <div className="h-12 bg-gray-800 border-b border-gray-700 flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <Button
                variant={previewMode === 'desktop' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewMode('desktop')}
                className="text-white"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Desktop
              </Button>
              <Button
                variant={previewMode === 'mobile' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewMode('mobile')}
                className="text-white"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Mobile
              </Button>
            </div>
          </div>

          {/* Live Preview Area */}
          <div className="flex-1 overflow-auto flex items-start justify-center p-4">
            <div 
              className={`
                border border-gray-700 overflow-y-auto
                ${previewMode === 'mobile' 
                  ? 'w-96 h-[600px] max-w-sm' 
                  : 'w-full h-full max-w-5xl'
                }
              `}
            >
              <LivePreview
                profile={currentProfile}
                profileData={profileData}
                profileType={profileType}
                isMobile={previewMode === 'mobile'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* History Modal */}
      {showHistory && (
        <HistoryModal
          history={history}
          onClose={() => setShowHistory(false)}
          onRestore={(snapshot) => {
            setCurrentProfile({ ...snapshot });
            setIsDirty(true);
            setShowHistory(false);
            toast.success('Profile restored from history');
          }}
        />
      )}

      {/* Discard Confirmation */}
      {showDiscardConfirm && (
        <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white">
            <div className="p-6">
              <h3 className="text-lg font-bold mb-4">Discard Changes?</h3>
              <p className="text-gray-400 mb-6">
                You have unsaved changes that will be lost. Are you sure you want to discard them?
              </p>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDiscardConfirm(false)}
                  className="border-gray-600 text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDiscard}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Discard Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Theme Editor Component
const ThemeEditor = ({ theme, onThemeUpdate, contrastWarning }) => {
  return (
    <div className="space-y-6">
      {/* Contrast Warning */}
      {contrastWarning && (
        <div className="bg-yellow-600/20 border border-yellow-600/30 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-yellow-400 text-sm">{contrastWarning}</div>
          </div>
        </div>
      )}

      {/* Theme Presets */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white">Theme Presets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(THEME_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => onThemeUpdate({
                  preset: key,
                  primary_color: preset.primary,
                  accent_color: preset.accent,
                  background_style: preset.background,
                  font_preset: theme.font_preset || 'bold_street'
                })}
                className={`
                  p-3 rounded-lg border-2 transition-all text-left
                  ${theme.preset === key 
                    ? 'border-orange-500 bg-orange-500/10' 
                    : 'border-gray-600 hover:border-gray-500'
                  }
                `}
              >
                <div 
                  className="w-full h-8 rounded mb-2"
                  style={{ 
                    background: preset.background === 'gradient' 
                      ? `linear-gradient(135deg, ${preset.primary}, ${preset.accent})`
                      : preset.primary 
                  }}
                />
                <div className="text-xs font-medium text-white">{preset.name}</div>
                <div className="text-xs text-gray-400">Contrast: {preset.contrast}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Colors */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white">Custom Colors</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="primary-color" className="text-white">Primary Color</Label>
            <div className="flex items-center space-x-3 mt-2">
              <input
                type="color"
                id="primary-color"
                value={theme.primary_color || "#f97316"}
                onChange={(e) => onThemeUpdate({ primary_color: e.target.value })}
                className="w-12 h-12 rounded-lg border-2 border-gray-600 cursor-pointer"
              />
              <Input
                value={theme.primary_color || "#f97316"}
                onChange={(e) => onThemeUpdate({ primary_color: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white flex-1"
                placeholder="#f97316"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="accent-color" className="text-white">Accent Color</Label>
            <div className="flex items-center space-x-3 mt-2">
              <input
                type="color"
                id="accent-color"
                value={theme.accent_color || "#ef4444"}
                onChange={(e) => onThemeUpdate({ accent_color: e.target.value })}
                className="w-12 h-12 rounded-lg border-2 border-gray-600 cursor-pointer"
              />
              <Input
                value={theme.accent_color || "#ef4444"}
                onChange={(e) => onThemeUpdate({ accent_color: e.target.value })}
                className="bg-gray-700 border-gray-600 text-white flex-1"
                placeholder="#ef4444"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Font Presets */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white">Typography</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(FONT_PRESETS).map(([key, font]) => (
              <button
                key={key}
                onClick={() => onThemeUpdate({ font_preset: key })}
                className={`
                  w-full p-3 rounded-lg border-2 transition-all text-left
                  ${theme.font_preset === key 
                    ? 'border-orange-500 bg-orange-500/10' 
                    : 'border-gray-600 hover:border-gray-500'
                  }
                `}
              >
                <div className="font-bold text-white" style={{ fontWeight: font.weight }}>
                  {font.name}
                </div>
                <div className="text-sm text-gray-400">{font.heading} / {font.body}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Banner Upload */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white">Banner Image</CardTitle>
        </CardHeader>
        <CardContent>
          <FileUpload
            type="banner"
            currentUrl={theme.banner_url}
            onUploadComplete={(result) => onThemeUpdate({ banner_url: result.url })}
            className="w-full"
          />
        </CardContent>
      </Card>
    </div>
  );
};

// Layout Editor Component (will continue in next message due to length)
const LayoutEditor = ({ layout, onLayoutUpdate, onWidgetReorder, onWidgetToggle }) => {
  return (
    <div className="space-y-6">
      {/* Template Selection */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white">Layout Template</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'single_column', name: 'Single Column', desc: 'Centered layout' },
              { key: 'two_column', name: 'Two Column', desc: 'Sidebar layout' }
            ].map((template) => (
              <button
                key={template.key}
                onClick={() => onLayoutUpdate({ template: template.key })}
                className={`
                  p-4 rounded-lg border-2 transition-all text-left
                  ${layout.template === template.key 
                    ? 'border-orange-500 bg-orange-500/10' 
                    : 'border-gray-600 hover:border-gray-500'
                  }
                `}
              >
                <div className="text-white font-medium">{template.name}</div>
                <div className="text-sm text-gray-400">{template.desc}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Widget Order */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white">Widget Order</CardTitle>
        </CardHeader>
        <CardContent>
          <DragDropContext onDragEnd={onWidgetReorder}>
            <Droppable droppableId="widgets">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                  {(layout.widgets || DEFAULT_WIDGETS)
                    .sort((a, b) => a.order - b.order)
                    .map((widget, index) => (
                      <Draggable key={widget.id} draggableId={widget.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`
                              flex items-center justify-between p-3 rounded-lg border transition-all
                              ${snapshot.isDragging 
                                ? 'border-orange-500 bg-orange-500/10' 
                                : 'border-gray-600 bg-gray-700'
                              }
                            `}
                          >
                            <div className="flex items-center space-x-3">
                              <div {...provided.dragHandleProps}>
                                <GripVertical className="w-4 h-4 text-gray-400" />
                              </div>
                              <span className="text-white font-medium">{widget.title}</span>
                            </div>
                            <Switch
                              checked={widget.enabled}
                              onCheckedChange={() => onWidgetToggle(widget.id)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </CardContent>
      </Card>
    </div>
  );
};

// Widgets, Privacy, LivePreview, and HistoryModal components would continue here...
// For brevity, I'll include placeholders that can be expanded

const WidgetsEditor = ({ widgets, layout, onWidgetUpdate, profileType }) => {
  const enabledWidgets = (layout.widgets || DEFAULT_WIDGETS).filter(w => w.enabled);

  return (
    <div className="space-y-6">
      {enabledWidgets.map((widget) => {
        const widgetData = widgets[widget.id] || {};

        switch (widget.type) {
          case 'about':
            return (
              <Card key={widget.id} className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-white">About Widget</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-white">Bio (400 characters max)</Label>
                    <Textarea
                      value={widgetData.bio || ''}
                      onChange={(e) => onWidgetUpdate('about', { bio: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white mt-2"
                      placeholder="Tell your audience about yourself..."
                      rows={4}
                      maxLength={400}
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {(widgetData.bio || '').length}/400 characters
                    </div>
                  </div>
                  <div>
                    <Label className="text-white">Location</Label>
                    <Input
                      value={widgetData.location || ''}
                      onChange={(e) => onWidgetUpdate('about', { location: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white mt-2"
                      placeholder="Your city, country"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Tagline</Label>
                    <Input
                      value={widgetData.tagline || ''}
                      onChange={(e) => onWidgetUpdate('about', { tagline: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white mt-2"
                      placeholder="Your creative motto"
                    />
                  </div>
                </CardContent>
              </Card>
            );

          case 'series_shelf':
          case 'library_shelf':
            return (
              <Card key={widget.id} className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-white">
                    {profileType === 'creator' ? 'Series Shelf' : 'Library Shelf'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-white">Sort Order</Label>
                    <select
                      value={widgetData.sort_type || 'popular'}
                      onChange={(e) => onWidgetUpdate(widget.id, { sort_type: e.target.value })}
                      className="w-full mt-2 p-2 bg-gray-700 border border-gray-600 rounded text-white"
                    >
                      <option value="popular">Most Popular</option>
                      <option value="recent">Most Recent</option>
                      <option value="manual">Manual Order</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white">Maximum Items</Label>
                    <Input
                      type="number"
                      value={widgetData.max_items || 6}
                      onChange={(e) => onWidgetUpdate(widget.id, { max_items: parseInt(e.target.value) })}
                      className="bg-gray-700 border-gray-600 text-white mt-2"
                      min="1"
                      max="12"
                    />
                  </div>
                </CardContent>
              </Card>
            );

          case 'links':
            return (
              <Card key={widget.id} className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-white">Social Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {['instagram', 'twitter', 'youtube', 'twitch', 'website'].map((platform) => (
                    <div key={platform}>
                      <Label className="text-white capitalize">{platform}</Label>
                      <Input
                        value={widgetData[platform] || ''}
                        onChange={(e) => onWidgetUpdate('links', { ...widgetData, [platform]: e.target.value })}
                        className="bg-gray-700 border-gray-600 text-white mt-2"
                        placeholder={`https://${platform}.com/yourusername`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            );

          case 'fan_wall':
            return (
              <Card key={widget.id} className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-white">Fan Wall</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-white">Enable Fan Wall</Label>
                    <Switch
                      checked={widgetData.enabled !== false}
                      onCheckedChange={(enabled) => onWidgetUpdate('fan_wall', { enabled })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-white">Auto-approve Posts</Label>
                    <Switch
                      checked={widgetData.auto_approve !== false}
                      onCheckedChange={(auto_approve) => onWidgetUpdate('fan_wall', { auto_approve })}
                    />
                  </div>
                  <div>
                    <Label className="text-white">Blocked Words (one per line)</Label>
                    <Textarea
                      value={(widgetData.blocked_words || []).join('\n')}
                      onChange={(e) => onWidgetUpdate('fan_wall', { 
                        blocked_words: e.target.value.split('\n').filter(word => word.trim()) 
                      })}
                      className="bg-gray-700 border-gray-600 text-white mt-2"
                      placeholder="spam&#10;inappropriate&#10;blocked"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            );

          default:
            return null;
        }
      })}
      
      {enabledWidgets.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-400">No widgets enabled. Enable widgets in the Layout tab.</div>
        </div>
      )}
    </div>
  );
};

const PrivacyEditor = ({ privacy, onPrivacyUpdate }) => {
  return (
    <div className="space-y-6">
      {/* Profile Visibility */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white">Profile Visibility</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { value: 'public', label: 'Public', desc: 'Anyone can view your profile' },
              { value: 'followers', label: 'Followers Only', desc: 'Only your followers can view' },
              { value: 'private', label: 'Private', desc: 'Only you can view' }
            ].map((option) => (
              <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value={option.value}
                  checked={(privacy.profile_visibility || 'public') === option.value}
                  onChange={(e) => onPrivacyUpdate({ profile_visibility: e.target.value })}
                  className="mt-1"
                />
                <div>
                  <div className="text-white font-medium">{option.label}</div>
                  <div className="text-sm text-gray-400">{option.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Widget Visibility */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white">Widget Visibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {DEFAULT_WIDGETS.map((widget) => (
            <div key={widget.id} className="flex items-center justify-between">
              <Label className="text-white">{widget.title}</Label>
              <select
                value={privacy.widget_visibility?.[widget.id] || 'public'}
                onChange={(e) => onPrivacyUpdate({ 
                  widget_visibility: { 
                    ...privacy.widget_visibility, 
                    [widget.id]: e.target.value 
                  }
                })}
                className="p-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              >
                <option value="public">Public</option>
                <option value="followers">Followers</option>
                <option value="private">Private</option>
              </select>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

const LivePreview = ({ profile, profileData, profileType, isMobile }) => {
  if (!profile || !profileData) {
    return (
      <div className="bg-black text-white p-4 h-full flex items-center justify-center">
        <div className="text-gray-400">Loading preview...</div>
      </div>
    );
  }

  const theme = profile.theme || {};
  const themeColors = {
    primary: theme.primary_color || '#f97316',
    accent: theme.accent_color || '#ef4444'
  };

  return (
    <div 
      className="bg-black text-white min-h-full"
      style={{ 
        background: theme.background_style === 'gradient' 
          ? `linear-gradient(135deg, ${themeColors.primary}20, ${themeColors.accent}20)`
          : '#000000'
      }}
    >
      {/* Preview Banner */}
      <div className="relative">
        <div 
          className={`${isMobile ? 'h-32' : 'h-48'} bg-cover bg-center`}
          style={{
            backgroundImage: theme.banner_url 
              ? `url('${BACKEND_URL}${theme.banner_url}')`
              : `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.accent})`
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>
        
        {/* Preview Avatar */}
        <div className="absolute -bottom-8 left-6">
          <div 
            className={`${isMobile ? 'w-16 h-16' : 'w-24 h-24'} rounded-full border-4 border-white bg-gray-800 overflow-hidden`}
          >
            {profileData.avatar_url ? (
              <img 
                src={`${BACKEND_URL}${profileData.avatar_url}`}
                alt={profileData.display_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: themeColors.primary }}
              >
                {(profileData.display_name || 'U').slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Content */}
      <div className="pt-12 px-6 pb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-white`}>
              {profileData.display_name || 'Your Name'}
            </h1>
            <p className="text-gray-400">@{profileData.handle || 'yourhandle'}</p>
            <Badge 
              className="mt-2 capitalize"
              style={{ backgroundColor: themeColors.primary }}
            >
              {profileType}
            </Badge>
          </div>
        </div>

        {/* Preview Widgets */}
        <div className="space-y-6">
          {(profile.layout?.widgets || DEFAULT_WIDGETS)
            .filter(w => w.enabled)
            .sort((a, b) => a.order - b.order)
            .map((widget) => {
              const widgetData = profile.widgets?.[widget.id] || {};
              
              return (
                <Card key={widget.id} className="bg-gray-900 border-gray-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white">{widget.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {widget.type === 'about' && (
                      <div className="space-y-2">
                        <p className="text-gray-300">
                          {widgetData.bio || "Tell your audience about yourself..."}
                        </p>
                        {widgetData.location && (
                          <p className="text-sm text-gray-400">📍 {widgetData.location}</p>
                        )}
                        {widgetData.tagline && (
                          <p className="text-sm font-medium text-orange-400">"{widgetData.tagline}"</p>
                        )}
                      </div>
                    )}
                    
                    {(widget.type === 'series_shelf' || widget.type === 'library_shelf') && (
                      <div className="grid grid-cols-2 gap-3">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="aspect-[3/4] bg-gray-800 rounded-lg">
                            <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                              {profileType === 'creator' ? 'Series' : 'Comic'} {i}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {widget.type === 'links' && (
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(widgetData).map(([platform, url]) => (
                          url && (
                            <Badge key={platform} variant="outline" className="capitalize">
                              {platform}
                            </Badge>
                          )
                        ))}
                        {Object.keys(widgetData).length === 0 && (
                          <p className="text-gray-500 text-sm">No social links added yet</p>
                        )}
                      </div>
                    )}
                    
                    {widget.type === 'fan_wall' && (
                      <div className="space-y-3">
                        {widgetData.enabled === false ? (
                          <p className="text-gray-500 text-sm">Fan wall is disabled</p>
                        ) : (
                          <>
                            <div className="p-3 bg-gray-800 rounded-lg">
                              <p className="text-gray-300 text-sm">Amazing work! Love your style! 🔥</p>
                            </div>
                            <div className="p-3 bg-gray-800 rounded-lg">
                              <p className="text-gray-300 text-sm">Can't wait for the next episode!</p>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
        </div>

        {/* Preview Draft Badge */}
        <div className="mt-8 text-center">
          <Badge className="bg-blue-600 text-white">
            🎨 Preview Mode - Changes not yet published
          </Badge>
        </div>
      </div>
    </div>
  );
};

const HistoryModal = ({ history, onClose, onRestore }) => {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[80vh]">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold">Version History</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {history.length > 0 ? (
              history
                .slice()
                .reverse()
                .map((entry, index) => (
                  <div key={entry.version} className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge className="bg-blue-600 text-white text-xs">
                            v{entry.version}
                          </Badge>
                          <span className="text-sm text-gray-400">
                            {new Date(entry.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-white font-medium">{entry.note}</p>
                        <div className="text-sm text-gray-400 mt-1">
                          Theme: {entry.snapshot.theme?.preset || 'default'} • 
                          Widgets: {entry.snapshot.layout?.widgets?.filter(w => w.enabled).length || 0} enabled
                        </div>
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const previewUrl = `${window.location.origin}${window.location.pathname}?preview=history&version=${entry.version}`;
                            window.open(previewUrl, '_blank');
                          }}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            onRestore(entry.snapshot);
                          }}
                          className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                        >
                          Restore
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No version history available</p>
                <p className="text-sm text-gray-500 mt-2">
                  Your profile edits will be saved here for easy restoration
                </p>
              </div>
            )}
          </div>
          
          {history.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-700">
              <p className="text-sm text-gray-500">
                💡 Tip: You can restore any previous version to continue editing from that point
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileEditorModal;